/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Person } from "fhir/r5";
import { TermOfUseDialog } from "../pages/TermsOfUse/TermOfUseDialog";
import { useRelatedPersons } from "../hooks/session/useRelatedPersons";
import { useDeviceRegistration } from "../hooks/session/useDeviceRegistration";
import { RefreshTokenGoogle } from "../services/google/RefreshTokenGoogle";
import {
  useRoles,
  type IRole,
  type AccessRole,
} from "../hooks/session/useRoles";
import { useTerms } from "../hooks/session/useTerms";
import { useUserData } from "@/hooks/session/useUserData";
import { isMobile } from "react-device-detect";
import { installGoogleMobileOAuthListener } from "@/services/google/googleMobileOAuthBridge";
// Removed provider wrappers to avoid import cycles; kept only session-related imports

interface IAccess {
  type?: "patient" | "professional" | null;
  organizationId?: string;
  roles?: AccessRole[];
  // Quando o usuário é sócio de uma associação (CNPJ), guardamos aqui o CNPJ-alvo
  // para filtrar telas de configuração (ex.: profissionais) sem depender de heurísticas locais.
  associatedCnpjDigits?: string;
  /**
   * Secretariado exclusivo (sem owner/practitioner/sócio) com PractitionerRoleAssociation na org atual.
   * Usado para restringir agendas no cliente ao mesmo CNPJ da extensão (a busca FHIR não filtra por extensão).
   */
  managerAssociationScopeCnpjDigits?: string;
}

interface SessionContextValue {
  getRolesArray: Function;
  fetchUser: any;
  fetchUserRoles: any;
  setRefetch: Function;
  setAccess: Function;
  setIsOpenTermDialog: Function;
  setInviteDialog: any;
  isOpenTermDialog: boolean;
  user: any;
  roles: IRole[] | undefined;
  access: IAccess;
  loading: boolean;
  userPatient: any;
  inviteDialog: any;
  relatedPerson: any;
  patientRelated: any;
  guardianRelated: any;
  person: Person;
  firstAccessDialog: any;
  setFirstAccessDialog: any;
  termsDialog: any;
  setTerminateAccount: Function;
  terminateAccount: any;
  termRefused: boolean;
  userNavigable: boolean | null;
  userMaintainer: boolean | null;
  accessTokenGoogle: string | null;
  setAccessTokenGoogle: Function;
}

export const SessionContext = createContext<SessionContextValue>(null!);

interface SessionContextProviderProps {
  children: React.ReactNode;
}

export function SessionContextProvider({
  children,
}: SessionContextProviderProps) {
  const navigate = useNavigate();
  const { user, userPatient, loading: userLoading, fetchUser } = useUserData();
  const {
    relatedPerson,
    patientRelated,
    guardianRelated,
    loading: relatedLoading,
    fetchRelatedData,
  } = useRelatedPersons({ identifier: user?.username });
  const { updateDeviceIdWeb } = useDeviceRegistration();
  const {
    roles,
    loading: rolesLoading,
    fetchUserRoles,
    getRolesArray,
  } = useRoles();
  const {
    person,
    termsDialog,
    isOpenTermDialog,
    terminateAccount,
    termRefused,
    loading: termsLoading,
    setIsOpenTermDialog,
    setTerminateAccount,
    fetchPersonData,
  } = useTerms();

  const [accessTokenGoogle, setAccessTokenGoogle] = useState<string | null>(
    null,
  );
  const [inviteDialog, setInviteDialog] = useState(false);
  const [firstAccessDialog, setFirstAccessDialog] = useState(true);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [access, setAccess] = useState<IAccess>(
    JSON.parse(localStorage.getItem("access") || "{}") as IAccess,
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [stableLoading, setStableLoading] = useState(true);
  const loadingStartTime = useRef<number | null>(null);
  const MIN_LOADING_TIME = 800; // Tempo mínimo que o loading deve ficar visível

  const userNavigable: boolean | null =
    roles?.some(
      (role) =>
        role.isOwner ||
        role.isManager ||
        role.isPractitioner ||
        role.isAssociated,
    ) ?? null;

  const userMaintainer: boolean | null =
    roles?.some((role) => role.isOwner || role.isManager) ?? null;

  const updateStableLoading = useCallback((isLoading: boolean) => {
    if (isLoading) {
      if (!loadingStartTime.current) {
        loadingStartTime.current = Date.now();
      }
      setStableLoading(true);
    } else {
      const currentTime = Date.now();
      const elapsedTime = loadingStartTime.current
        ? currentTime - loadingStartTime.current
        : 0;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      // Garante um tempo mínimo de exibição do loading
      const timer = setTimeout(() => {
        setStableLoading(false);
        loadingStartTime.current = null;
      }, remainingTime);

      return () => clearTimeout(timer);
    }
  }, []);

  const compositeLoading =
    initialLoading ||
    access.type === undefined ||
    userLoading ||
    relatedLoading ||
    rolesLoading ||
    termsLoading;

  useEffect(() => {
    updateStableLoading(compositeLoading);
  }, [compositeLoading, updateStableLoading]);

  async function loadUserData() {
    loadingStartTime.current = Date.now();
    try {
      setStableLoading(true);
      const userData = await fetchUser();

      if (userData?.extractScim2Data) {
        // Atualiza o token do Google se necessário
        if (
          userData?.extractScim2Data?.refreshTokenGoogle &&
          !accessTokenGoogle
        ) {
          try {
            const googleTokenData = await RefreshTokenGoogle(
              userData.extractScim2Data.refreshTokenGoogle,
            );
            setAccessTokenGoogle(googleTokenData.access_token);
          } catch (error) {
            console.error(
              "Erro ao renovar token do Google no primeiro acesso:",
              error,
            );
          }
        }
        // Executa todas as chamadas em paralelo e aguarda todas terminarem
        await Promise.all([
          fetchUserRoles(userData.extractScim2Data.username),

          fetchRelatedData(userData.patient),
          updateDeviceIdWeb(
            userData.extractScim2Data.tokenFirebaseWeb,
            userData.extractScim2Data.id,
          ),
          fetchPersonData(userData.extractScim2Data.username),
        ]);
      }
    } catch (err) {
      localStorage.clear();
      navigate("/signin");
      toast.warn(
        "Suas credenciais expiraram. Por favor faça o login novamente!",
      );
    } finally {
      // Não seta initialLoading como false imediatamente
      const currentTime = Date.now();
      const elapsedTime = loadingStartTime.current
        ? currentTime - loadingStartTime.current
        : 0;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      setTimeout(() => {
        setInitialLoading(false);
      }, remainingTime);
    }
  }

  useEffect(() => {
    if (localStorage.getItem("logged") === "true") {
      loadUserData();
    } else {
      setInitialLoading(false);
    }
  }, [refetch, access?.type]);

  // Listener global: processa retorno OAuth do app nativo e salva tokens no SCIM.
  useEffect(() => {
    installGoogleMobileOAuthListener({
      getUserId: () => user?.id,
      fetchUser,
      setAccessTokenGoogle,
    });
  }, [user?.id, fetchUser, setAccessTokenGoogle]);

  useEffect(() => {
    // Só executa quando o loading das roles estiver finalizado
    if (rolesLoading || initialLoading) return;

    if (isMobile) {
      setAccess((prev) => ({
        type: "patient",
      }));
      return;
    }

    // Se está em processo de convite, não altera o tipo
    if (roles?.find((el: any) => el.isInvited)) {
      setInviteDialog(true);
      return;
    }

    // Se está setando como professional, não interfere
    if (access.type === "professional") {
      const professionalRole = roles?.find((el: IRole) =>
        el.organization?.reference.includes(access.organizationId),
      );

      if (professionalRole) {
        const associationDigits = professionalRole?.associationCnpjDigits
          ? String(professionalRole.associationCnpjDigits).replace(/\D/g, "")
          : "";
        const isManagerOnlyScopedToAssociationCnpj =
          professionalRole?.isManager === true &&
          professionalRole?.isOwner !== true &&
          professionalRole?.isPractitioner !== true &&
          professionalRole?.isAssociated !== true &&
          Boolean(associationDigits);

        setAccess((prev) => ({
          ...prev,
          roles: getRolesArray(professionalRole),
          associatedCnpjDigits:
            professionalRole?.isAssociated &&
            professionalRole?.associationCnpjDigits
              ? String(professionalRole.associationCnpjDigits)
              : undefined,
          managerAssociationScopeCnpjDigits:
            isManagerOnlyScopedToAssociationCnpj
              ? associationDigits
              : undefined,
        }));
      }
      return;
    }

    // Se tem roles profissionais, não seta como paciente
    if (
      roles?.some(
        (role) => role.isPractitioner || role.isManager || role.isOwner,
      )
    ) {
      return;
    }

    // Se não tem roles E tem userPatient, então é paciente
    if ((!roles || roles.length === 0) && userPatient) {
      setAccess((prev) => ({
        ...prev,
        type: "patient",
      }));
      return;
    }
  }, [
    roles,
    access.type,
    access.organizationId,
    getRolesArray,
    userPatient,
    rolesLoading,
    initialLoading,
  ]);

  useEffect(() => {
    localStorage.setItem("access", JSON.stringify(access));
  }, [access]);

  const dialogIsOpen = !person?.active && !isOpenTermDialog;

  const contextValue: SessionContextValue = {
    getRolesArray,
    fetchUser,
    fetchUserRoles,
    setRefetch,
    setAccess,
    setInviteDialog,
    setFirstAccessDialog,
    setIsOpenTermDialog,
    setTerminateAccount,
    firstAccessDialog,
    isOpenTermDialog,
    user,
    roles,
    access,
    loading: stableLoading,
    inviteDialog,
    userPatient,
    relatedPerson,
    patientRelated,
    guardianRelated,
    person,
    termsDialog,
    terminateAccount,
    termRefused,
    userNavigable,
    userMaintainer,
    accessTokenGoogle,
    setAccessTokenGoogle,
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {termsDialog &&
        !stableLoading &&
        (userNavigable ? (
          <TermOfUseDialog isOpen={dialogIsOpen && !termRefused} update />
        ) : (
          <TermOfUseDialog isOpen={dialogIsOpen && !userNavigable} update />
        ))}
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  return useContext(SessionContext);
}
